import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Compass, Home, ChevronLeft, ArrowLeft, Lock, Loader2, ExternalLink, Newspaper, Plus, Edit2, Trash2, Save, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { AuthContext, API } from "@/App";
import { toast } from "sonner";

const NewsPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);

  const tier = user?.subscription_tier || "free";
  const hasAccess = user?.is_admin || user?.is_vip || ["premium", "team"].includes(tier);
  const isAdmin = user?.is_admin;

  // Admin state
  const [allNews, setAllNews] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [newsForm, setNewsForm] = useState({ title: "", content: "", category: "General", source_url: "", status: "draft" });

  useEffect(() => { fetchNews(); }, []);

  const fetchNews = async () => {
    try {
      const res = await fetch(`${API}/news`, { credentials: "include" });
      if (res.ok) setNews(await res.json());
      if (isAdmin) {
        const adminRes = await fetch(`${API}/admin/news`, { credentials: "include" });
        if (adminRes.ok) setAllNews(await adminRes.json());
      }
    } catch (e) {} finally { setLoading(false); }
  };

  const handleSaveNews = async () => {
    try {
      const method = editingNews ? "PUT" : "POST";
      const url = editingNews ? `${API}/admin/news/${editingNews.news_id}` : `${API}/admin/news`;
      const res = await fetch(url, {
        method, headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(newsForm)
      });
      if (res.ok) { toast.success(editingNews ? "Updated!" : "Created!"); setShowEditor(false); setEditingNews(null); fetchNews(); }
      else toast.error("Failed to save");
    } catch (e) { toast.error("Something went wrong"); }
  };

  const handleDeleteNews = async (newsId) => {
    if (!window.confirm("Delete this news item?")) return;
    try {
      const res = await fetch(`${API}/admin/news/${newsId}`, { method: "DELETE", credentials: "include" });
      if (res.ok) { toast.success("Deleted!"); fetchNews(); }
    } catch (e) { toast.error("Something went wrong"); }
  };

  const openEditor = (item = null) => {
    if (item) {
      setEditingNews(item);
      setNewsForm({ title: item.title, content: item.content, category: item.category, source_url: item.source_url || "", status: item.status });
    } else {
      setEditingNews(null);
      setNewsForm({ title: "", content: "", category: "General", source_url: "", status: "draft" });
    }
    setShowEditor(true);
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <Lock className="w-12 h-12 text-purple-400 mx-auto mb-4" />
          <h2 className="font-inter font-bold text-xl mb-2">News Feed</h2>
          <p className="text-white/50 text-sm mb-6">Stay updated with AI news. Available for Premium subscribers.</p>
          <Link to="/pricing"><Button className="bg-purple-700 text-white hover:bg-purple-600">Upgrade to Premium</Button></Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark">
      <header className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white/60 hover:text-white"><ChevronLeft className="w-5 h-5" /></Button>
              <Link to="/" className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-purple-700 flex items-center justify-center"><Compass className="w-4 h-4 text-white" /></div>
                <span className="font-inter font-bold text-lg">promptviser</span>
              </Link>
              <Badge className="bg-amber-500/10 text-amber-400 border-amber-500/30 text-[10px]">News</Badge>
            </div>
            <div className="flex items-center gap-2">
              {isAdmin && <Button onClick={() => openEditor()} className="bg-purple-700 text-white hover:bg-purple-600 text-xs" data-testid="create-news"><Plus className="w-3.5 h-3.5 mr-1" />Add News</Button>}
              <Link to="/"><Button variant="ghost" size="icon" className="text-white/60 hover:text-white"><Home className="w-5 h-5" /></Button></Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-white/40 hover:text-white mb-6 text-sm"><ArrowLeft className="w-4 h-4" />Dashboard</Link>

        <h1 className="font-inter font-bold text-2xl mb-2">AI News Feed</h1>
        <p className="text-white/40 text-sm mb-8">Latest updates on AI tools, features, and pricing changes.</p>

        {/* Admin: All news including drafts */}
        {isAdmin && allNews.length > 0 && (
          <div className="mb-8">
            <h2 className="font-semibold text-sm text-white/50 mb-3">Admin: All Items ({allNews.length})</h2>
            <div className="space-y-2">
              {allNews.filter(n => n.status !== "approved").map(item => (
                <div key={item.news_id} className="bg-dark-paper border border-amber-500/20 rounded-lg p-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge className={`text-[10px] ${item.status === "draft" ? "bg-white/10 text-white/40" : item.status === "rejected" ? "bg-red-500/10 text-red-400" : "bg-neon/10 text-neon"}`}>{item.status}</Badge>
                    <span className="text-sm">{item.title}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant="ghost" size="sm" onClick={() => openEditor(item)}><Edit2 className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDeleteNews(item.news_id)} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Published News */}
        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-6 h-6 text-purple-400 animate-spin mx-auto" /></div>
        ) : news.length === 0 ? (
          <div className="text-center py-16 bg-dark-paper border border-white/10 rounded-xl">
            <Newspaper className="w-10 h-10 text-white/15 mx-auto mb-3" />
            <h3 className="font-semibold mb-1">No news yet</h3>
            <p className="text-white/40 text-sm">Check back soon for AI updates.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {news.map(item => (
              <article key={item.news_id} className="bg-dark-paper border border-white/10 rounded-xl p-5 card-hover" data-testid={`news-${item.news_id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant="outline" className="text-[10px] border-white/20">{item.category}</Badge>
                      <span className="text-[10px] text-white/30">{new Date(item.published_at).toLocaleDateString()}</span>
                    </div>
                    <h3 className="font-inter font-semibold text-lg mb-2">{item.title}</h3>
                    <p className="text-white/50 text-sm leading-relaxed">{item.content}</p>
                    {item.source_url && (
                      <a href={item.source_url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-purple-400 mt-3 hover:underline">
                        Read source <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEditor(item)}><Edit2 className="w-3.5 h-3.5" /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteNews(item.news_id)} className="text-red-400"><Trash2 className="w-3.5 h-3.5" /></Button>
                    </div>
                  )}
                </div>
              </article>
            ))}
          </div>
        )}
      </main>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className="bg-dark-paper border-white/10 max-w-lg">
          <DialogHeader><DialogTitle className="text-sm">{editingNews ? "Edit News" : "Create News"}</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <div><Label className="text-xs">Title</Label><Input value={newsForm.title} onChange={e => setNewsForm({...newsForm, title: e.target.value})} className="bg-dark-subtle border-white/10 text-sm" /></div>
            <div><Label className="text-xs">Content</Label><Textarea value={newsForm.content} onChange={e => setNewsForm({...newsForm, content: e.target.value})} className="bg-dark-subtle border-white/10 text-sm min-h-[100px]" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={newsForm.category} onValueChange={v => setNewsForm({...newsForm, category: v})}>
                  <SelectTrigger className="bg-dark-subtle border-white/10 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["General","AI Updates","Pricing","New Features","SEO","Coding","Research"].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Status</Label>
                <Select value={newsForm.status} onValueChange={v => setNewsForm({...newsForm, status: v})}>
                  <SelectTrigger className="bg-dark-subtle border-white/10 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="approved">Approved (Publish)</SelectItem>
                    <SelectItem value="rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label className="text-xs">Source URL (optional)</Label><Input value={newsForm.source_url} onChange={e => setNewsForm({...newsForm, source_url: e.target.value})} className="bg-dark-subtle border-white/10 text-sm" placeholder="https://..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)} className="border-white/20 text-xs">Cancel</Button>
            <Button onClick={handleSaveNews} className="bg-purple-700 text-white hover:bg-purple-600 text-xs" disabled={!newsForm.title}><Save className="w-3.5 h-3.5 mr-1" />{editingNews ? "Update" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewsPage;
